import {
  GSI_SCRIPT_LOADED,
  API_CALL_SUCCESS,
  CLEAR_AUTH_ERROR, RESET_CUSTOMER_TRAVELLER_SUBMISSION, SET_CUSTOMER_VALUE,
  SET_RECENTLY_USED_AIRPORT_CODE,
  SYNC_USER_DETAILS, API_CALL_FAILURE,
  API_CALL, SAVE_CUSTOMER_SIGNATURE, DELETE_CUSTOMER_SIGNATURE,
  SET_RESOLVED_TRACKING_ID,
} from './actionConstants'
import {
  FETCH_AVAILABLE_CREDIT,
  FETCH_PAYMENT_CARDS,
  USER_LOGIN,
  USER_REGISTRATION,
  UPDATE_USER_DETAILS,
  USER_LOGOUT,
  UPDATE_ACCOUNT_PARTNERSHIP,
  USER_FORGOT_PASSWORD,
  LINK_PARTNERSHIP_ACCOUNT,
  FETCH_STRIPE_PAYMENT_CARDS,
  CHECK_LEGACY_ORDERS_FOR_CURRENT_USER,
  CHECK_USER_EXISTS,
  USER_RESET_PASSWORD,
  FETCH_CUSTOMER_DETAILS,
  DELETE_CUSTOMER_DETAILS,
  UPDATE_CUSTOMER_DETAILS,
  SEND_VERIFICATION_CODE,
  VERIFY_SMS_CODE,
  SEND_SIGNUP_OTP_CODE,
  VERIFY_SIGNUP_OTP_CODE,
  VALIDATE_REGISTRATION,
  UPDATE_ADD_PHONE_PROMPTED,
  FETCH_CUSTOMER_TRAVELLERS,
  DELETE_CUSTOMER_TRAVELLER,
  SUBMIT_CUSTOMER_TRAVELLER,
  USER_EMAIL_CONFIRMATION,
  FETCH_SIMILAR_ACCOUNTS,
} from './apiActionConstants'
import { getAvailableCredit, getSavedCardsFromStripe, getLedLegacyOrders, getSavedCardsOnStripe } from 'api/order'
import { getSavedStripeCards } from 'api/payment'
import * as authService from 'api/auth'
import { sendResetPasswordEmail, updatePartnershipDetails, resetPassword, requestUserEmailValidation, getSimilarAccounts } from 'api/auth'
import { setTimeUserLastSeenSnackbar } from 'storage/creditsSnackbar'
import { getCreditSnackbar, shouldShowCreditSnackbar } from 'lib/customer/creditSnackbarUtils'
import { replaceWithRegion } from './NavigationActions'
import { deleteCustomerDetails, getCustomerDetails, requestTravelCompanions, updateCustomerDetails, deleteCustomerTraveller, updateCustomerTraveller, createCustomerTraveller } from 'api/traveller'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type ReCAPTCHA from 'react-google-recaptcha'
import { isStripePaymentElementCardAvailable } from 'checkout/selectors/payment/paymentType'
import { replace } from 'connected-react-router'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import type { AppAction } from './types/ActionTypes'
import config from 'constants/config'
import { getStitchedSeedId, stitchedSeedIdCookieName } from 'analytics/snowplow/helpers/stitchedSeedId'
import { getSeedIdFromApi } from 'api/lab'
import { setCookie } from 'lib/web/cookieUtils'
import { getDomainUserId } from 'analytics/snowplow/helpers/domainUserId'
import { DurationSeconds } from 'lib/datetime/durationInSeconds'
import { rebucketOptimizelyExperiments } from './OptimizelyActions'
import { getCurrentIntl } from 'i18n/util/currentIntl'

const HIGH_VALUE_OFFER_AMOUNT = 4999
const MID_VALUE_OFFER_AMOUNT = 2999

export function getUserKeyFromPhone(phoneNumber: App.PhoneNumber): string {
  return `+${phoneNumber.prefix}${phoneNumber.phone}`
}

export function checkUserExists(
  user: { email?: string, phoneNumber?: App.PhoneNumber },
  recaptchaApi?: ReCAPTCHA | null,
): AppAction {
  return (dispatch, getState) => {
    const key = user.email ?? (user.phoneNumber ? getUserKeyFromPhone(user.phoneNumber) : undefined)
    if (!key) {
      return
    }
    const state = getState()
    // no need to check again, we've already done so
    if (state.auth.users[key]) {
      return
    }

    dispatch({
      type: API_CALL,
      api: CHECK_USER_EXISTS,
      request: async() => {
        let recaptchaData: string | null | undefined
        if (recaptchaApi) {
          recaptchaData = await recaptchaApi.executeAsync()
        }
        return authService.checkUserExists(user, recaptchaData)
      },
      email: user.email,
      phoneNumber: user.phoneNumber,
      key,
    })
  }
}

export function loginNativeApp(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.webViewAccountAccess(state.geo.currentRegionCode, getIsLuxPlusEnabled(state)),
    })
  } }

export function login(
  user: App.User,
  password: string,
  recaptchaData?: string,
): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.login(user, password, recaptchaData, state.geo.currentRegionCode, luxPlusEnabled),
      source: user.email ? 'email' : 'phone',
    })
  }
}

export interface GoogleLoginData {
  id_google: string
  email: string
  token_id: string
  givenName: string
  surname: string
}

export function loginGoogle(data: GoogleLoginData): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.loginGoogle(data, state.geo.currentRegionCode, luxPlusEnabled),
      source: 'google',
    })
  }
}

export interface FacebookLoginData {
  app_id: string
  email: string
  access_token: string
  givenName: string
  surname: string
}

export function loginFacebook(data: FacebookLoginData): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)

    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.loginFacebook(data, state.geo.currentRegionCode, luxPlusEnabled),
      source: 'facebook',
    })
  }
}

export interface AppleLoginData {
  id_token: string
  givenName?: string
  firstName?: string
}

export function loginApple(data: AppleLoginData): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)

    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.loginApple(data, state.geo.currentRegionCode, luxPlusEnabled),
      source: 'apple',
    })
  }
}

const registerMethods = {
  phone: authService.register,
  email: authService.register,
  google: authService.loginGoogle,
  facebook: authService.loginFacebook,
  apple: authService.loginApple,
}

export function register(userData: Partial<App.AuthAccount> & { password: string }, source: App.SignUpAuthSource): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const registerData = {
      ...userData,
      utmAdgroup: state.utm.adgroup,
      utmCampaign: state.utm.campaign,
      utmContent: state.utm.content,
      utmMedium: state.utm.medium,
      utmSource: state.utm.source,
      utmTerm: state.utm.term,
    }

    dispatch({
      type: API_CALL,
      api: USER_REGISTRATION,
      source,
      request: () => registerMethods[source]((registerData as any), state.geo.currentRegionCode),
    })
  }
}

export function verifyUserEmail(token: string, showErrorSnackbar = true): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: USER_EMAIL_CONFIRMATION,
      request: async() => {
        const intl = getCurrentIntl()
        const verifyUserErrorMessage = intl.formatMessage({
          id: 'CpEUG8uzOa_MP-33s1o_',
          defaultMessage: 'We couldn’t verify your email, please try again.',
          description: 'Error message shown when email verification fails',
        })
        try {
          await requestUserEmailValidation({ token, brand: config.BRAND })
          showSnackbar(intl.formatMessage({
            id: 'DuZ3NdsGanCCcB6Dy1Eb',
            defaultMessage: 'Your email has been successfully verified.',
            description: 'Snackbar message after successful email verification',
          }), 'success')
          dispatch(replaceWithRegion('/'))
        } catch {
          if (showErrorSnackbar) showSnackbar(verifyUserErrorMessage, 'critical')
          dispatch({
            type: API_CALL_FAILURE,
            api: USER_EMAIL_CONFIRMATION,
            error: { message: verifyUserErrorMessage },
          })
        }
      },
    })
  }
}

export function resetUserPassword(token: string, password: string, callbackPath?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    dispatch({
      type: API_CALL,
      api: USER_RESET_PASSWORD,
      request: () => {
        const intl = getCurrentIntl()
        return resetPassword({ token, password, luxPlusEnabled }).then(data => {
          showSnackbar(intl.formatMessage({
            id: 'UMkm_2lJnXEesd0anHqY',
            defaultMessage: 'Your password has been successfully reset',
            description: 'Snackbar message after successful password reset',
          }), 'success')
          if (callbackPath) {
            dispatch(replace(callbackPath))
          } else {
            dispatch(replaceWithRegion('/'))
          }

          dispatch({
            type: API_CALL_SUCCESS,
            api: USER_LOGIN,
            data,
          })

          return data
        })
      },
    })
  }
}

export function forgotPassword(email: string, callbackPath?: string): AppAction {
  return {
    type: API_CALL,
    api: USER_FORGOT_PASSWORD,
    request: () => sendResetPasswordEmail(email, callbackPath),
  }
}

export function linkPartnershipAccount(partnershipPrefix: string, details: any): AppAction {
  return {
    type: API_CALL,
    api: LINK_PARTNERSHIP_ACCOUNT,
    request: () => updatePartnershipDetails({ [partnershipPrefix]: details }),
    prefix: partnershipPrefix,
    details,
  }
}

export function authLogout(): AppAction {
  return {
    type: API_CALL,
    api: USER_LOGOUT,
    request: () => authService.logout(),
  }
}

/**
 * Updates the resolved tracking ID after login by checking for an existing stitched seed ID
 * or fetching a new one from the API. This ensures consistent user identification for
 * Optimizely bucketing and Snowplow analytics. Falls back to domainUserId if stitched ID
 * cannot be obtained.
 *
 * Triggers Optimizely re-bucketing whenever this tab's resolvedTrackingId actually changes
 * (e.g. switching from an anonymous domainUserId to an authenticated stitched seed ID).
 * This covers both the originating tab (which fetches the seed from the API) and receiving
 * tabs (which find the cookie already written by another tab via BroadcastChannel login).
 */
export function updateResolvedTrackingId(): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const memberId = state.auth.account.memberId
    const domainUserId = getDomainUserId()
    const previousResolvedTrackingId = state.auth.resolvedTrackingId

    // Check if there's already a stitched seed ID cookie for this member
    let stitchedSeedId = getStitchedSeedId(memberId)

    // If not, fetch from API and set cookie
    if (!stitchedSeedId && memberId) {
      try {
        stitchedSeedId = await getSeedIdFromApi(memberId)
        if (stitchedSeedId) {
          // Re-check cookie after async API call — another tab (via BroadcastChannel login)
          // may have fetched and set the cookie while we were waiting for the API response.
          // If so, skip setting the cookie again (it's already persisted).
          const existingCookie = getStitchedSeedId(memberId)
          if (!existingCookie) {
            setCookie(stitchedSeedIdCookieName(memberId), stitchedSeedId, DurationSeconds.ONE_YEAR, '/', 'Lax')
          }
        }
      } catch(error) {
        console.error('Failed to fetch stitched seed ID:', error)
      }
    }

    // Use stitched seed ID if available, otherwise fallback to domainUserId
    const resolvedTrackingId = stitchedSeedId ?? domainUserId
    if (resolvedTrackingId) {
      dispatch({
        type: SET_RESOLVED_TRACKING_ID,
        resolvedTrackingId,
      })
    }

    // Re-bucket Optimizely experiments whenever this tab's resolved tracking ID changes.
    // This handles both:
    //  1. The originating tab (fetched seed from API, cookie was not yet set)
    //  2. Receiving tabs (cookie already written by another tab via BroadcastChannel login,
    //     but this tab's Redux state was still bucketed under the anonymous ID)
    const trackingIdChanged = resolvedTrackingId && resolvedTrackingId !== previousResolvedTrackingId
    if (trackingIdChanged) {
      dispatch(rebucketOptimizelyExperiments())
    }
  }
}

export function fetchAvailableCreditAndOpenSnackbar(currencyCode?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const memberId = state.auth.account.memberId
    if (!memberId) { return }

    dispatch({
      type: API_CALL,
      api: FETCH_AVAILABLE_CREDIT,
      request: () => getAvailableCredit(
        memberId,
        currencyCode ?? state.geo.currentCurrency,
      ).then(resp => {
        if (!resp.balance) return resp
        const {
          geo: { currentCurrency, currentRegionCode },
          router: { location: { pathname } },
          system: { headlessMode },
        } = state

        if (shouldShowCreditSnackbar(resp.balance, currentCurrency, pathname, memberId, headlessMode)) {
          const creditSnackbar = getCreditSnackbar(getCurrentIntl(), resp.balance, currentCurrency, currentRegionCode)
          showSnackbar(creditSnackbar.message, 'success', { heading: creditSnackbar.heading })
          setTimeUserLastSeenSnackbar(memberId)
        }
        return resp
      }),
      currencyCode: currencyCode ?? state.geo.currentCurrency,
    })
  }
}

export function fetchAvailableCredit(currencyCode?: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const memberId = state.auth.account.memberId
    if (!memberId) { return }

    dispatch({
      type: API_CALL,
      api: FETCH_AVAILABLE_CREDIT,
      request: () => getAvailableCredit(
        memberId,
        currencyCode ?? state.geo.currentCurrency,
      ),
      currencyCode: currencyCode ?? state.geo.currentCurrency,
    })
  }
}

export function openCreditSnackbar(): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const {
      geo: { currentCurrency, currentRegionCode },
      router: { location: { pathname } },
      auth: { account: { balance, memberId } },
      system: { headlessMode },
    } = state

    if (memberId && shouldShowCreditSnackbar(balance, currentCurrency, pathname, memberId, headlessMode)) {
      const creditSnackbar = getCreditSnackbar(getCurrentIntl(), balance, currentCurrency, currentRegionCode)
      showSnackbar(creditSnackbar.message, 'success', { heading: creditSnackbar.heading })
      setTimeUserLastSeenSnackbar(memberId)
    }
  }
}

export function setRecentlyUsedAirportCode(airportCode: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.auth.account.memberId) {
      dispatch({
        type: API_CALL,
        api: SET_RECENTLY_USED_AIRPORT_CODE,
        request: () => authService.updateUserDetails({ recentlyUsedAirportCode: airportCode }),
        airportCode,
      })
    }
  }
}

export function updateAddPhonePromptedAt(addPhonePromptedAt: Date): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (state.auth.account.memberId) {
      dispatch({
        type: API_CALL,
        api: UPDATE_ADD_PHONE_PROMPTED,
        request: () => authService.updateAddPhonePromptedAt(addPhonePromptedAt),
        addPhonePromptedAt: addPhonePromptedAt.toISOString(),
      })
    }
  }
}

export function fetchPaymentCards(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const customerId = state.auth.account.memberId
    if (customerId && !isStripePaymentElementCardAvailable(state)) {
      dispatch({
        type: API_CALL,
        api: FETCH_PAYMENT_CARDS,
        request: () =>
          getSavedCardsFromStripe(customerId),
      })
    }
  }
}

export function fetchStripePaymentCards(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (isStripePaymentElementCardAvailable(state)) {
      dispatch({
        type: API_CALL,
        api: FETCH_STRIPE_PAYMENT_CARDS,
        request: () => getSavedStripeCards(state.geo.currentCurrency),
      })
    } else {
      const customerId = state.auth.account.memberId
      if (!customerId) return
      dispatch({
        type: API_CALL,
        api: FETCH_STRIPE_PAYMENT_CARDS,
        request: () => getSavedCardsOnStripe(customerId),
      })
    }
  }
}

/**
 * Saves the details given for the current user in the API and updates state
 * with the newest details
 * @param userDetails The user details to save to the API
 * @param supportPhoneNumber Optional support phone number
 * @returns
 */
export function updateUserDetails(
  userDetails: Partial<App.AuthAccount>,
  supportPhoneNumber: string | null = null,
): AppAction {
  return {
    type: API_CALL,
    api: UPDATE_USER_DETAILS,
    request: () => authService.updateUserDetails(userDetails, supportPhoneNumber),
  }
}

/**
 * Manually sync a set of user details into redux
 * Used for when you absolutely have to call the update api method yourself
 * @param userDetails The user details to sync to redux
 */
export function syncUserDetails(userDetails: Partial<App.AuthAccount>): AppAction {
  return {
    type: SYNC_USER_DETAILS,
    userDetails,
  }
}

export function updateAccountPartnership(partnershipPrefix: string, details: App.PartnershipsMap): AppAction {
  return {
    type: API_CALL,
    api: UPDATE_ACCOUNT_PARTNERSHIP,
    request: () => authService.updatePartnershipDetails(details).then(() => details),
    partnershipPrefix,
  }
}

export function hasLegacyOrdersForCurrentUser(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { memberId } = state.auth.account
    if (memberId && !state.auth.ledLegacyOrders.hasFetched) {
      dispatch({
        type: API_CALL,
        api: CHECK_LEGACY_ORDERS_FOR_CURRENT_USER,
        request: () => getLedLegacyOrders(memberId),
      })
    }
  }
}

export function fetchTravellerDetails(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { memberId } = state.auth.account
    if (!memberId) { return }
    dispatch({
      type: API_CALL,
      api: FETCH_CUSTOMER_DETAILS,
      request: () => getCustomerDetails(memberId),
    })
  }
}

export function deleteTravellerDetails(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { memberId } = state.auth.account
    if (!memberId) { return }
    dispatch({
      type: API_CALL,
      api: DELETE_CUSTOMER_DETAILS,
      request: () => deleteCustomerDetails(memberId),
    })
  }
}

export function updateTravellerDetails(travellerDetails: App.CustomerDetails): AppAction {
  return {
    type: API_CALL,
    api: UPDATE_CUSTOMER_DETAILS,
    request: () => updateCustomerDetails({
      ...travellerDetails,
    }),
  }
}

export function saveCustomerSignature(signature: string): AppAction {
  return {
    type: SAVE_CUSTOMER_SIGNATURE,
    signature,
  }
}

export function deleteCustomerSignature(): AppAction {
  return {
    type: DELETE_CUSTOMER_SIGNATURE,
  }
}

export function GSILoadComplete(): AppAction {
  return {
    type: GSI_SCRIPT_LOADED,
  }
}

export function sendOTPVerificationCode(user: App.User): AppAction {
  return {
    type: API_CALL,
    api: SEND_VERIFICATION_CODE,
    request: () => authService.sendOTPVerificationCode(user),
  }
}

export function verifySmsCodeAction(code: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const regionCode = state.geo.currentRegionCode
    const luxPlusEnabled = getIsLuxPlusEnabled(state)

    const requestPromise = authService
      .verifySmsCode(code)
      .then(({ accessToken, token }) =>
        authService.me(accessToken, regionCode, luxPlusEnabled).then((account) => ({
          accessToken: accessToken ?? state.auth.accessToken,
          account,
          token,
        })),
      )

    dispatch({
      type: API_CALL,
      api: VERIFY_SMS_CODE,
      request: () => requestPromise,
    })

    return requestPromise
  }
}

export function validateOTPVerificationCode(user: App.User, code: string): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const luxPlusEnabled = getIsLuxPlusEnabled(state)
    dispatch({
      type: API_CALL,
      api: USER_LOGIN,
      request: () => authService.validateOTPVerificationCode(user, code, state.geo.currentRegionCode, luxPlusEnabled),
      source: 'otp',
    })
  }
}

export function sendSignUpOTPCode(phoneNumber: App.PhoneNumber): AppAction {
  return {
    type: API_CALL,
    api: SEND_SIGNUP_OTP_CODE,
    request: () => authService.sendSignUpOTPCode(phoneNumber),
  }
}

export function verifySignUpOTPCode(phoneNumber: App.PhoneNumber, code: string): AppAction {
  return {
    type: API_CALL,
    api: VERIFY_SIGNUP_OTP_CODE,
    request: () => authService.verifySignUpOTPCode(phoneNumber, code),
  }
}

export function validateRegistration(userData: Partial<App.AuthAccount> & { password: string }): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const registerData = {
      ...userData,
    }

    dispatch({
      type: API_CALL,
      api: VALIDATE_REGISTRATION,
      request: () => authService.validateRegistration(registerData, state.geo.currentRegionCode),
    })
  }
}

export function registerWithOTP(userData: Partial<App.AuthAccount> & { password: string }): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    const registerData = {
      ...userData,
      utmAdgroup: state.utm.adgroup,
      utmCampaign: state.utm.campaign,
      utmContent: state.utm.content,
      utmMedium: state.utm.medium,
      utmSource: state.utm.source,
      utmTerm: state.utm.term,
    }

    dispatch({
      type: API_CALL,
      api: USER_REGISTRATION,
      source: 'email',
      request: () => authService.registerWithOTP(registerData, state.geo.currentRegionCode),
    })
  }
}

export function setCustomerValue(price: number): AppAction {
  const value = price >= HIGH_VALUE_OFFER_AMOUNT ? 'highValue' : price >= MID_VALUE_OFFER_AMOUNT ? 'midValue' : undefined
  return {
    type: SET_CUSTOMER_VALUE,
    value,
  }
}

/**
 * Call to clear any existing auth errors
 */
export function clearAuthError(): AppAction {
  return {
    type: CLEAR_AUTH_ERROR,
  }
}

interface CustomerTravellersFetchOptions {
  refetch?: boolean
}
export function fetchTravelCompanions(options: CustomerTravellersFetchOptions = {}): AppAction {
  return (dispatch, getState) => {
    const state = getState()

    if (!state.auth.travelCompanions.initial && !options.refetch) return
    if (state.auth.travelCompanions.fetching && !options.refetch) return
    if (state.auth.travelCompanions.error && !options.refetch) return

    dispatch({
      type: API_CALL,
      api: FETCH_CUSTOMER_TRAVELLERS,
      request: () => requestTravelCompanions(),
    })
  }
}

export function destroyCustomerTravellerDetails(id: string): AppAction {
  return (dispatch) => {
    if (!id) { return }
    dispatch({
      type: API_CALL,
      api: DELETE_CUSTOMER_TRAVELLER,
      id,
      request: () => deleteCustomerTraveller(id),
    })
  }
}

export function createCustomerTravellerDetails(
  customerTraveller: Omit<App.TravelCompanion, 'id'>,
  options: {
    onCreated?: (companion: App.TravelCompanion) => void
  } = {},
): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: SUBMIT_CUSTOMER_TRAVELLER,
      request: () => createCustomerTraveller(customerTraveller).then(companion => {
        options.onCreated?.(companion)
        dispatch(fetchTravelCompanions({ refetch: true }))
        return companion
      }),
    })
  }
}

export function updateCustomerTravellerDetails(customerTraveller: App.TravelCompanion): AppAction {
  return (dispatch) => {
    dispatch({
      type: API_CALL,
      api: SUBMIT_CUSTOMER_TRAVELLER,
      request: () => updateCustomerTraveller(customerTraveller).then(companion => {
        dispatch(fetchTravelCompanions({ refetch: true }))
        return companion
      }),
    })
  }
}

export function resetCustomerTravellerSubmission(): AppAction {
  return {
    type: RESET_CUSTOMER_TRAVELLER_SUBMISSION,
  }
}

export function fetchSimilarAccounts(): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    const { memberId } = state.auth.account
    if (!memberId) { return }
    dispatch({
      type: API_CALL,
      api: FETCH_SIMILAR_ACCOUNTS,
      request: () => getSimilarAccounts(),
    })
  }
}
